<template>
  <v-card :loading="loading">
    <v-card-text class="d-flex flex-wrap align-center">
      <v-select
          v-model="selectedId"
          :items="retakeDates"
          :item-text="dateLabel"
          item-value="id"
          label="Nachschreibetermin"
          no-data-text="Keine Nachschreibetermine im Zeitplan"
          class="retake-date-select mr-4 mb-2"
          hide-details
          @change="loadAssignments"/>
      <v-text-field
          v-model="search"
          prepend-inner-icon="mdi-magnify"
          label="Schüler, Klasse oder Fach suchen"
          class="mb-2"
          single-line
          hide-details
          clearable/>
    </v-card-text>

    <v-card-text v-if="selected && !selected.underway" class="pt-0">
      <v-alert type="info" dense text class="mb-0">
        Die Anwesenheit kann ab {{ selected.early_start_minutes }} Minuten vor Beginn und während des Nachschreibetermins erfasst
        werden. Bis dahin ist die Liste eine Vorschau, die sich mit der Anwesenheit früherer Termine ändert.
      </v-alert>
    </v-card-text>

    <v-data-table
        :headers="headers"
        :items="visibleAssignments"
        item-key="key"
        :loading="loading"
        group-by="class_name"
        disable-pagination
        disable-sort
        hide-default-footer
        loading-text="Daten werden geladen..."
        :no-data-text="search ? 'Keine Treffer' : 'Keine Schüler für diesen Nachschreibetermin'"
        class="elevation-1">
      <template v-slot:[`group.header`]="{ group, headers }">
        <td :colspan="headers.length" class="font-weight-bold">{{ group }}</td>
      </template>
      <template v-slot:[`item.student_name`]="{ item }">
        <div v-if="isFirstRow(item)" class="d-flex align-center">
          <v-avatar size="48" class="mr-3">
            <v-img lazy-src="/images/no-avatar.jpg" :src="avatars[item.student_id]"/>
          </v-avatar>
          {{ item.student_name }}
        </div>
      </template>
      <template v-slot:[`item.subject_name`]="{ item }">
        <v-select
            v-if="item.open_exams.length > 1"
            :value="item.key"
            :items="choicesFor(item)"
            :disabled="!editable || isSaving(item)"
            class="exam-select"
            dense
            hide-details
            @change="(key) => changeExam(item, key)"/>
        <span v-else>{{ examLabel(item) }}</span>
        <v-btn
            v-if="editable && isLastRow(item) && unusedExams(item).length"
            x-small
            text
            color="primary"
            class="px-0"
            @click="addExam(item)">
          + weitere Klausur
        </v-btn>
      </template>
      <template v-slot:[`item.missed_retakes`]="{ item }">
        <div class="d-flex flex-wrap">
          <v-chip v-if="item.status === 'certificate_due'" small dark color="orange" class="mr-1 my-1">
            <v-icon left small>mdi-file-clock-outline</v-icon>
            Warten auf Attest bis {{ item.certificate_deadline | moment("DD.MM.") }}
          </v-chip>
          <v-chip v-if="item.missed_retakes" small outlined color="orange" class="mr-1 my-1">
            bereits {{ item.missed_retakes }}× verpasst
          </v-chip>
          <v-chip v-if="item.admitted" small outlined color="primary" class="mr-1 my-1">
            von Lehrkraft angemeldet
          </v-chip>
        </div>
      </template>
      <template v-slot:[`item.present`]="{ item }">
        <div class="d-flex align-center">
          <v-radio-group
              :value="item.present"
              row
              :disabled="!editable || isSaving(item)"
              @change="(present) => record(item, present)">
            <v-radio label="Ja" :value="true"></v-radio>
            <v-radio label="Nein" :value="false"></v-radio>
          </v-radio-group>
          <v-btn
              v-if="editable && rowsOf(item.student_id).length > 1"
              icon
              small
              title="Klausur entfernen"
              :disabled="isSaving(item)"
              @click="removeExam(item)">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>
      </template>
    </v-data-table>
  </v-card>
</template>

<script lang="ts">
import {Component, Vue} from "vue-property-decorator";
import {useHttp} from "@/core/http";
import {examKey, OpenExam, RetakeAssignment, RetakeDate} from "@/core/models/missing-exam";
import {accentInsensitiveTableFilter} from "@/core/utils/search";

// Moment runs without a German locale in the class book.
const WEEKDAYS = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa']

// One row per exam a student writes at the Termin.
type Row = RetakeAssignment & { key: string }

@Component
export default class RetakeAttendance extends Vue {
  private loading = false
  private saving: Record<string, boolean> = {}
  private retakeDates: Array<RetakeDate> = []
  private selectedId: number | null = null
  private assignments: Array<Row> = []
  private avatars: Record<number, string> = {}
  private search: string | null = ''
  private headers: Array<Record<string, any>> = [
    {text: 'Schüler', value: 'student_name'},
    {text: 'Klausur', value: 'subject_name'},
    {text: 'Hinweis', value: 'missed_retakes'},
    {text: 'Anwesend', value: 'present'},
  ]

  private http = useHttp()

  private get selected(): RetakeDate | null {
    return this.retakeDates.find((date) => date.id === this.selectedId) ?? null
  }

  // Every row of a student who matches, so the student's name stays on the first row.
  private get visibleAssignments(): Array<Row> {
    if (!this.search) {
      return this.assignments
    }

    const matching = new Set(this.assignments
        .filter((row) => [row.student_name, row.class_name, row.subject_name]
            .some((value) => accentInsensitiveTableFilter(value, this.search)))
        .map((row) => row.student_id))

    return this.assignments.filter((row) => matching.has(row.student_id))
  }

  private get editable(): boolean {
    return this.selected?.underway === true
  }

  private async mounted() {
    this.loading = true

    try {
      const response: any = await this.http.get('missing-exams/retake-dates')
      this.retakeDates = response.data
      this.selectedId = this.defaultDate()?.id ?? null
    } catch {
      this.notifyError('Die Nachschreibetermine konnten nicht geladen werden.')
    } finally {
      this.loading = false
    }

    await this.loadAssignments()
  }

  // The running Termin, else the next one, else the last one held.
  private defaultDate(): RetakeDate | undefined {
    const now = this.$moment().format('YYYY-MM-DD HH:mm')

    return this.retakeDates.find((date) => date.underway)
        ?? this.retakeDates.find((date) => (date.end ?? `${(date.start ?? '').slice(0, 10)} 23:59`) >= now)
        ?? this.retakeDates[this.retakeDates.length - 1]
  }

  private loadAssignments(): Promise<void> {
    if (this.selectedId === null) {
      this.assignments = []
      return Promise.resolve()
    }

    this.loading = true

    return this.http.get(`missing-exams/retake-dates/${this.selectedId}`)
        .then((response: any) => this.applyResponse(response))
        .catch(() => {
          this.assignments = []
          this.notifyError('Die Schüler des Nachschreibetermins konnten nicht geladen werden.')
        })
        .finally(() => {
          this.loading = false
        })
  }

  private isSaving(row: Row): boolean {
    return this.saving[row.key]
  }

  private rowsOf(studentId: number): Array<Row> {
    return this.assignments.filter((row) => row.student_id === studentId)
  }

  private isFirstRow(row: Row): boolean {
    return this.rowsOf(row.student_id)[0] === row
  }

  private isLastRow(row: Row): boolean {
    const rows = this.rowsOf(row.student_id)

    return rows[rows.length - 1] === row
  }

  private unusedExams(row: Row): Array<OpenExam> {
    const used = this.rowsOf(row.student_id).map((other) => other.key)

    return row.open_exams.filter((exam) => !used.includes(examKey(row.student_id, exam)))
  }

  // The row's own exam and those no other row of the student shows yet, oldest first.
  private choicesFor(row: Row): Array<{ value: string, text: string }> {
    const unused = this.unusedExams(row)

    return row.open_exams
        .filter((exam) => examKey(row.student_id, exam) === row.key || unused.includes(exam))
        .map((exam) => ({value: examKey(row.student_id, exam), text: this.examLabel(exam)}))
  }

  private examLabel(exam: OpenExam): string {
    return `${exam.subject_name} · ${this.$moment(exam.date).format('DD.MM.YYYY')}`
  }

  private addExam(row: Row): void {
    const exam = this.unusedExams(row)[0]

    this.assignments.splice(this.assignments.indexOf(row) + 1, 0, this.rowFor(row, exam, null))
  }

  // Moves a recorded attendance over to the newly chosen exam; an unrecorded row only changes on screen.
  private async changeExam(row: Row, key: string): Promise<void> {
    const exam = row.open_exams.find((open) => examKey(row.student_id, open) === key)
    if (!exam) {
      return
    }

    const replacement = this.rowFor(row, exam, row.present)

    if (row.present !== null) {
      const saved = await this.save(row, () => this.http.destroy(this.attendanceUrl(row, row))
          .then(() => this.http.put(this.attendanceUrl(row), this.examPayload(exam, row.present))))

      // The old record may already be gone, so show what is stored now.
      if (!saved) {
        await this.loadAssignments()
        return
      }
    }

    this.assignments.splice(this.assignments.indexOf(row), 1, replacement)
  }

  private async removeExam(row: Row): Promise<void> {
    if (row.present !== null && !await this.save(row, () => this.http.destroy(this.attendanceUrl(row, row)))) {
      return
    }

    this.assignments.splice(this.assignments.indexOf(row), 1)
  }

  // Saves each choice as it is made; a refused one is undone so the radio shows what is stored.
  private async record(row: Row, present: boolean): Promise<void> {
    const previous = row.present
    row.present = present

    if (!await this.save(row, () => this.http.put(this.attendanceUrl(row), this.examPayload(row, present)))) {
      row.present = previous
    }
  }

  private async save(row: Row, request: () => Promise<unknown>): Promise<boolean> {
    this.$set(this.saving, row.key, true)

    try {
      await request()
      return true
    } catch (error: any) {
      this.notifyError(error?.data?.message ?? 'Die Anwesenheit konnte nicht gespeichert werden.')

      // The Termin may have ended while the page was open.
      if (error?.status === 403) {
        await this.loadAssignments()
      }

      return false
    } finally {
      this.$set(this.saving, row.key, false)
    }
  }

  // The exam is named in the query string, as a DELETE request carries no body.
  private attendanceUrl(row: Row, exam?: OpenExam): string {
    const url = `missing-exams/retake-dates/${this.selectedId}/attendance/${row.student_id}`

    return exam ? `${url}?subject_id=${exam.subject_id}&exam_date=${exam.date}` : url
  }

  private examPayload(exam: OpenExam, present: boolean | null): Record<string, unknown> {
    return {subject_id: exam.subject_id, exam_date: exam.date, present}
  }

  private rowFor(row: Row, exam: OpenExam, present: boolean | null): Row {
    return {
      ...row,
      subject_id: exam.subject_id,
      subject_name: exam.subject_name,
      date: exam.date,
      status: exam.status,
      certificate_deadline: exam.certificate_deadline,
      admitted: exam.admitted,
      present,
      missed_retakes: row.subject_id === exam.subject_id && row.date === exam.date ? row.missed_retakes : 0,
      key: examKey(row.student_id, exam),
    }
  }

  private applyResponse(response: any): void {
    this.assignments = response.data.map((assignment: RetakeAssignment) => ({
      ...assignment,
      key: examKey(assignment.student_id, assignment),
    }))
    this.avatars = response.avatars ?? {}

    const index = this.retakeDates.findIndex((date) => date.id === response.retake_date?.id)
    if (index !== -1) {
      this.$set(this.retakeDates, index, response.retake_date)
    }
  }

  private dateLabel(date: RetakeDate): string {
    const start = this.$moment(date.start)
    const day = `${WEEKDAYS[start.day()]}, ${start.format('DD.MM.YYYY')}`
    const time = date.all_day
        ? 'ganztägig'
        : date.end ? `${start.format('HH:mm')}–${this.$moment(date.end).format('HH:mm')}` : `ab ${start.format('HH:mm')}`

    return [`${day} ${time}`, date.topic].filter(Boolean).join(' · ')
  }

  private notifyError(text: string): void {
    this.$notify({title: 'Fehler', type: 'error', text})
  }
}
</script>

<style scoped>
.retake-date-select {
  max-width: 32rem;
}

.exam-select {
  max-width: 18rem;
}
</style>
